import { apiGet, apiPost, apiPatch, apiPut, apiDelete } from "./client.ts";

export interface AdminStats {
  totals: {
    users: number;
    videos: number;
    activeSubscriptions: number;
    recentSignups: number;
    activeGateways: number;
    totalCreditsIssued: number;
  };
  videosByStatus: Array<{ status: string; count: number }>;
  usersByPlan: Array<{ plan: string; count: number }>;
}

export interface AdminUser {
  id: string;
  email: string;
  role: string;
  currentPlan: string | null;
  stripeCustomerId: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ProviderField {
  key: string;
  label: string;
  type: string;
  required: boolean;
  secret: boolean;
}

export interface GatewayProvider {
  id: string;
  name: string;
  configFields: ProviderField[];
}

// ── Provider system (Phase 3-admin) ──
export interface ProviderCatalogEntry {
  id: string;
  kind: "video" | "image" | "text";
  displayName: string;
  requiredCredentials: string[];
  optionalCredentials?: string[];
  defaultModels?: { preview?: string; final?: string };
  balanceHint?: string;
}

export interface ProviderConfig {
  id: string;
  moduleId: string;
  label: string;
  kind: "video" | "image" | "text";
  tier: "preview" | "final";
  modelSlug: string;
  isActive: boolean;
  priority: number;
  costPerGeneration: number;
  failoverEnabled: boolean;
  maxRetries: number;
  retryBackoffMs: number;
  isLastResort: boolean;
  // SPARK image-gen per-model overrides (image-kind rows only). Nullable — null/absent
  // means the feature is off for this model and it behaves as before.
  defaultNegatives?: string[] | null;
  renderParams?: Record<string, string | number | boolean> | null;
  renderClasses?: Array<"photoreal" | "graphic" | "text_heavy"> | null;
  // Provider capability flags (item 8) — the boolean features that drive
  // selection/skip-policy. Edited via checkboxes (never raw JSON) so the shape
  // is always a flat object of booleans. null/absent = unset (module defaults apply).
  capabilities?: {
    supports_lip_sync?: boolean;
    supports_audio?: boolean;
    supports_reference_image?: boolean;
    presenter_avatar?: boolean;
    diffusion_motion?: boolean;
    image_to_video?: boolean;
  } | null;
  createdAt: string;
  updatedAt: string;
}

export interface ProviderCredential {
  moduleId: string;
  credentials: Record<string, string>; // masked
  /** (tf3-32) the keys whose stored value couldn't be decrypted (shown "(couldn't decrypt)"); absent from an older backend */
  undecryptable?: string[];
  updatedAt: string;
}

export interface ProviderAttempt {
  id: string;
  moduleId: string;
  kind: string;
  tier: string | null;
  modelSlug: string | null;
  success: boolean;
  category: string | null;
  latencyMs: number | null;
  httpStatus: number | null;
  errorMessage: string | null;
  createdAt: string;
}

export interface ProviderAttemptSummary {
  total: number;
  success: number;
  failed: number;
  categories: Record<string, number>;
}

export interface ProviderAlert {
  moduleId: string;
  severity: "confirmed" | "suspected";
  count: number;
  firstSeen: string;
  lastSeen: string;
  categories: Record<string, number>;
}

export interface ProviderSpend {
  moduleId: string;
  unitCostCents: number;
  successes: number;
  estimatedCents: number;
  byDay: Record<string, number>;
  avgDailyCents: number;
}

export interface ProviderBalance {
  moduleId: string;
  displayName: string;
  balanceHint: string;
  hasBalance: boolean;
  needsBalance: boolean;
  balanceUsdCents: number | null;
  lowThresholdUsdCents: number | null;
  autoReloadEnabled: boolean | null;
  note: string | null;
  recordedAt: string | null;
  /** (audit 10-15) null when the spend couldn't be read (`spendError`): unknown, never $0 */
  avgDailyCents: number | null;
  estimatedWindowCents: number | null;
  runwayDays: number | null;
  warn: boolean;
}

export interface PaymentGateway {
  id: string;
  name: string;
  provider: string;
  isActive: boolean;
  isDefault: boolean;
  config: Record<string, unknown>;
  supportedCurrencies: string[];
  supportedCountries: string[] | null;
  displayOrder: number;
  createdAt: string;
  updatedAt: string;
}

export interface AuditEntry {
  id: number;
  adminId: string;
  action: string;
  targetType: string | null;
  targetId: string | null;
  details: Record<string, unknown> | null;
  ipAddress: string | null;
  createdAt: string;
}

export interface AdminCaptionStyle {
  id: string;
  slug: string;
  name: string;
  description: string | null;
  // Catalog fields (Phase 4). The admin list route returns all columns, so these
  // arrive automatically; optional-nullable for rows synced before they existed.
  previewUrl?: string | null;
  isDefault?: boolean;
  font: string;
  color: string;
  strokeColor: string | null;
  fontSizeShorts: number;
  fontSizeLong: number;
  chunkSizeShorts: number;
  chunkSizeLong: number;
  animation: string;
  kind: string;
  emphasis: string;
  placement: string;
  exit: string;
  uppercase: boolean;
  isActive: boolean;
  sortOrder: number;
}

export interface AdminStockAvatar {
  stockId: string;
  provider: string;
  providerAvatarId: string;
  groupId: string | null;
  avatarType: string | null;
  displayName: string | null;
  gender: string | null;
  previewUrl: string | null;
  defaultVoiceId: string | null;
  isEnabled: boolean;
  isDefault: boolean;
  isDelisted: boolean;
  syncedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface AdminPreset {
  id: string;
  tenantId: string | null;
  displayName: string;
  description: string;
  config: Record<string, unknown>;
  isActive: boolean;
  createdAt: string;
}

export interface AdminTenant {
  id: string;
  slug: string;
  domain: string;
  name: string;
  tagline: string | null;
  logoUrl: string | null;
  primaryColor: string;
  accentColor: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface AdminApiKey {
  id: string;
  tenantId: string;
  tenantName: string;
  keyPrefix: string;
  name: string;
  isActive: boolean;
  lastUsedAt: string | null;
  createdAt: string;
}

// ── Admin Videos ──
export interface AdminVideoRow {
  id: string;
  userId: string;
  userEmail: string | null;
  status: string;
  format: string;
  autopilot: boolean;
  scriptPreview: string | null;
  voiceoverUrl: string | null;
  finalVideoUrl: string | null;
  thumbnailUrls: string[] | null;
  selectedThumbnailUrl: string | null;
  errorMessage: string | null;
  creditsUsed: number;
  callbackUrl: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface AdminVideoDetail {
  id: string;
  userId: string;
  userEmail: string | null;
  status: string;
  format: string;
  autopilot: boolean;
  scriptText: string | null;
  voiceoverUrl: string | null;
  finalVideoUrl: string | null;
  thumbnailUrls: string[] | null;
  selectedThumbnailUrl: string | null;
  errorMessage: string | null;
  creditsUsed: number;
  callbackUrl: string | null;
  presetId: string;
  captionFont: string | null;
  captionColor: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface AdminSegment {
  id: string;
  orderIndex: number;
  text: string;
  imageUrl: string | null;
  imageModel: string | null;
  videoClipPreviewUrl: string | null;
  videoClipUrl: string | null;
  videoClipTaskId: string | null;
  durationMs: number | null;
}

export interface AdminProviderAttemptDetail {
  id: string;
  segmentId: string | null;
  moduleId: string;
  kind: string;
  tier: string | null;
  modelSlug: string | null;
  success: boolean;
  category: string | null;
  latencyMs: number | null;
  httpStatus: number | null;
  errorMessage: string | null;
  createdAt: string;
}

// ── Character library (CR-004 Phase 3) ──
export interface CharacterCapability {
  talkingHead: boolean;
  motion: boolean;
}

export interface CharacterListItem {
  stockId: string;
  provider: string;
  displayName: string | null;
  gender: string | null;
  ageRange: string | null;
  ethnicity: string | null;
  style: string | null;
  tags: string | null;
  previewUrl: string | null;
  voiceCatalogId: string;
  state: "pending" | "ready" | "failed";
  visibility: "public" | "private";
  owner: string | null;
  isEnabled: boolean;
  isDefault: boolean;
  isDelisted: boolean;
  createdAt: string;
  updatedAt: string;
  capability: CharacterCapability;
}

export interface CharacterKlingRef {
  position: number;
  url: string;
  type: string;
}

export interface CharacterAttributeView {
  key: string;
  valueText: string | null;
  valueNum: number | null;
  valueBool: boolean | null;
}

export interface CharacterDetailView {
  character: Omit<CharacterListItem, "capability">;
  capability: CharacterCapability;
  heygen: { providerAvatarId: string; groupId: string | null; avatarType: string | null } | null;
  klingRefs: CharacterKlingRef[];
  attributes: CharacterAttributeView[];
  /** (session 9) the character's own voice — every video it's in speaks with it */
  voice?: (VoiceOption & { status: string }) | null;
}

export interface CharacterBrowseFilters {
  gender?: string;
  ageRange?: string;
  ethnicity?: string;
  style?: string;
  state?: "pending" | "ready" | "failed";
  visibility?: "public" | "private";
  capability?: "talking_head" | "motion";
  search?: string;
  limit?: number;
  offset?: number;
}

export interface CharacterGenerationInput {
  gender: "F" | "M";
  ageRange: string;
  ethnicity: string;
  style: string;
  visualDescriptor?: string;
  niche?: string;
  physicalAttributes?: Record<string, string>;
  personas?: string[];
}

export interface AttributeCatalogValue {
  key: string;
  label: string;
}
export interface AttributeCatalogDimension {
  key: string;
  label: string;
  gender: "F" | "M" | null;
  values: AttributeCatalogValue[];
}

export interface VoiceOption {
  id: string;
  voiceId: string;
  name: string;
  provider: string;
  gender: string;
  /** (session 9) the stored preview to listen to (null until the preview sync has run), its tone and accent */
  sampleUrl?: string | null;
  tone?: string | null;
  accent?: string | null;
}

export const adminApi = {
  // Stats
  stats: () => apiGet<AdminStats>("/admin/stats"),

  // Videos
  listVideos: (params: { limit?: number; offset?: number; status?: string; search?: string; userId?: string } = {}) => {
    const qs = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => { if (v !== undefined) qs.set(k, String(v)); });
    const q = qs.toString();
    return apiGet<{ videos: AdminVideoRow[]; pagination: { limit: number; offset: number; total: number; count: number } }>(
      `/admin/videos${q ? `?${q}` : ""}`,
    );
  },
  getVideo: (id: string) => apiGet<{
    video: AdminVideoDetail;
    segments: AdminSegment[];
    providerAttempts: AdminProviderAttemptDetail[];
  }>(`/admin/videos/${id}`),
  getVideoStats: () => apiGet<{
    videosByStatus: Array<{ status: string; count: number }>;
    recentProviderAttempts: Array<{ moduleId: string; kind: string; success: boolean; count: number; avgLatency: number }>;
  }>(`/admin/videos/stats`),

  // Users
  listUsers: (params: { limit?: number; offset?: number; search?: string; plan?: string; role?: string } = {}) => {
    const qs = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => { if (v !== undefined) qs.set(k, String(v)); });
    const q = qs.toString();
    return apiGet<{ users: AdminUser[]; pagination: { limit: number; offset: number; total: number } }>(
      `/admin/users${q ? `?${q}` : ""}`,
    );
  },
  getUser: (id: string) => apiGet<{
    user: AdminUser;
    creditBalance: number;
    videos: Array<{ id: string; status: string; format: string; createdAt: string }>;
    subscriptions: Array<{ id: string; plan: string; status: string; createdAt: string }>;
    ledger: Array<{ id: number; delta: number; reason: string; balanceAfter: number; createdAt: string }>;
  }>(`/admin/users/${id}`),
  updateUser: (id: string, body: { role?: string; currentPlan?: string | null }) =>
    apiPatch<{ ok: boolean }>(`/admin/users/${id}`, body),
  adjustCredits: (id: string, body: { delta: number; reason: string }) =>
    apiPost<{ ok: boolean; newBalance: number }>(`/admin/users/${id}/credits`, body),

  // Gateways
  listGateways: () =>
    apiGet<{ gateways: PaymentGateway[]; availableProviders: GatewayProvider[] }>("/admin/gateways"),
  createGateway: (body: Record<string, unknown>) =>
    apiPost<{ ok: boolean; id: string }>("/admin/gateways", body),
  updateGateway: (id: string, body: Record<string, unknown>) =>
    apiPatch<{ ok: boolean }>(`/admin/gateways/${id}`, body),
  deleteGateway: (id: string) =>
    apiDelete<{ ok: boolean }>(`/admin/gateways/${id}`),
  testGateway: (id: string) =>
    apiPost<{ success: boolean; message: string; provider: string }>(`/admin/gateways/${id}/test`),

  // Settings
  getSettings: () => apiGet<{ settings: Record<string, { value: string; description: string | null; updatedAt: string }> }>("/admin/settings"),
  updateSettings: (body: Record<string, string>) =>
    apiPatch<{ ok: boolean; updatedKeys: string[] }>("/admin/settings", body),

  // Audit log
  auditLog: (params: { limit?: number; offset?: number } = {}) => {
    const qs = new URLSearchParams();
    if (params.limit) qs.set("limit", String(params.limit));
    if (params.offset) qs.set("offset", String(params.offset));
    const q = qs.toString();
    return apiGet<{ entries: AuditEntry[]; pagination: { limit: number; offset: number; total: number } }>(
      `/admin/settings/audit-log${q ? `?${q}` : ""}`,
    );
  },

  // Presets
  // (session 7, 7c: read-only — channel_presets is generated from the video-type catalogue)
  listPresets: () => apiGet<{ presets: AdminPreset[] }>("/admin/presets"),

  // Caption styles — the named looks users pick from when creating a video.
  // Deleting is refused server-side if a style is in use; deactivate instead.
  listCaptionStyles: () =>
    apiGet<{ styles: AdminCaptionStyle[] }>("/admin/caption-styles"),
  createCaptionStyle: (body: Record<string, unknown>) =>
    apiPost<{ ok: boolean; id?: string; slug?: string }>("/admin/caption-styles", body),
  updateCaptionStyle: (id: string, body: Record<string, unknown>) =>
    apiPatch<{ ok: boolean }>(`/admin/caption-styles/${id}`, body),
  deleteCaptionStyle: (id: string) =>
    apiDelete<{ ok: boolean }>(`/admin/caption-styles/${id}`),

  // Tenants
  listTenants: () => apiGet<{ tenants: AdminTenant[] }>("/admin/tenants"),
  createTenant: (body: Record<string, unknown>) =>
    apiPost<{ ok: boolean; id: string }>("/admin/tenants", body),
  updateTenant: (id: string, body: Record<string, unknown>) =>
    apiPatch<{ ok: boolean }>(`/admin/tenants/${id}`, body),
  deleteTenant: (id: string) =>
    apiDelete<{ ok: boolean }>(`/admin/tenants/${id}`),

  // API Keys
  listApiKeys: () => apiGet<{ keys: AdminApiKey[] }>("/admin/api-keys"),
  createApiKey: (body: { tenantId: string; name: string }) =>
    apiPost<{ key: string; prefix: string; tenantSlug: string; message: string }>("/admin/api-keys", body),
  deleteApiKey: (id: string) =>
    apiDelete<{ ok: boolean }>(`/admin/api-keys/${id}`),

  // Bulk user ops
  bulkUsers: (body: { action: string; userIds: string[] }) =>
    apiPost<{ ok: boolean; affected: number }>("/admin/users/bulk", body),

  // ── Providers (Phase 3-admin) ──
  listProviders: () =>
    apiGet<{ configs: ProviderConfig[]; catalog: ProviderCatalogEntry[] }>("/admin/providers"),
  createProvider: (body: Record<string, unknown>) =>
    apiPost<{ ok: boolean; id: string }>("/admin/providers", body),
  updateProvider: (id: string, body: Record<string, unknown>) =>
    apiPatch<{ ok: boolean }>(`/admin/providers/${id}`, body),
  deleteProvider: (id: string) => apiDelete<{ ok: boolean }>(`/admin/providers/${id}`),

  listProviderCredentials: () =>
    apiGet<{ credentials: ProviderCredential[]; catalog: ProviderCatalogEntry[] }>(
      "/admin/providers/credentials",
    ),
  upsertProviderCredentials: (moduleId: string, credentials: Record<string, string>) =>
    apiPost<{ ok: boolean }>(`/admin/providers/credentials/${moduleId}`, { credentials }),
  testProviderCredentials: (moduleId: string) =>
    apiPost<{ success: boolean; message: string; moduleId: string }>(
      `/admin/providers/credentials/${moduleId}/test`,
    ),
  testProviderGeneration: (moduleId: string) =>
    apiPost<{ success: boolean; message: string; moduleId: string }>(
      `/admin/providers/credentials/${moduleId}/test-generation`,
    ),
  providerAttempts: (moduleId?: string) =>
    apiGet<{ attempts: ProviderAttempt[]; summary: Record<string, ProviderAttemptSummary> }>(
      `/admin/providers/attempts${moduleId ? `?moduleId=${moduleId}` : ""}`,
    ),
  providerAlerts: (windowHours = 24) =>
    apiGet<{ windowHours: number; alerts: ProviderAlert[] }>(
      `/admin/providers/alerts?windowHours=${windowHours}`,
    ),
  providerSpend: (days = 7) =>
    apiGet<{ days: number; estimated: boolean; providers: ProviderSpend[] }>(
      `/admin/providers/spend?days=${days}`,
    ),
  providerBalances: (days = 7) =>
    apiGet<{ days: number; estimated: boolean; spendError: string | null; balances: ProviderBalance[] }>(
      `/admin/providers/balances?days=${days}`,
    ),
  updateProviderBalance: (
    moduleId: string,
    body: {
      balanceUsdCents: number;
      lowThresholdUsdCents?: number | null;
      autoReloadEnabled?: boolean;
      note?: string | null;
    },
  ) => apiPut<{ ok: boolean; moduleId: string }>(`/admin/providers/balances/${moduleId}`, body),

  // Stock avatars (shared HeyGen pool)
  listStockAvatars: () =>
    apiGet<{ avatars: AdminStockAvatar[] }>("/admin/stock-avatars"),
  syncStockAvatars: () =>
    apiPost<{ upserted: number; delisted: number; fetched: number }>("/admin/stock-avatars/sync"),
  updateStockAvatar: (stockId: string, body: { isEnabled?: boolean; isDefault?: boolean }) =>
    apiPatch<{ avatar: AdminStockAvatar | null }>(`/admin/stock-avatars/${stockId}`, body),

  // ── Character library (CR-004 Phase 3/4) ──
  browseCharacters: (filters: CharacterBrowseFilters = {}) => {
    const q = new URLSearchParams();
    for (const [k, v] of Object.entries(filters)) {
      if (v !== undefined && v !== null && v !== "") q.set(k, String(v));
    }
    const qs = q.toString();
    return apiGet<{
      characters: CharacterListItem[];
      pagination: { limit: number; offset: number; total: number };
    }>(`/admin/characters${qs ? `?${qs}` : ""}`);
  },
  getCharacter: (id: string) => apiGet<CharacterDetailView>(`/admin/characters/${id}`),
  resolveCharacters: (params: { capability?: "talking_head" | "motion"; visibility?: "public" | "private"; owner?: string; limit?: number } = {}) => {
    const q = new URLSearchParams();
    for (const [k, v] of Object.entries(params)) {
      if (v !== undefined && v !== null && v !== "") q.set(k, String(v));
    }
    const qs = q.toString();
    return apiGet<{ characters: Array<{ stockId: string; displayName: string | null; gender: string | null; visibility: string; owner: string | null; capability: CharacterCapability }> }>(
      `/admin/characters/resolve${qs ? `?${qs}` : ""}`,
    );
  },
  createCharacterGenerate: (body: {
    generation: CharacterGenerationInput;
    displayName?: string;
    voiceCatalogId: string;
    visibility?: "public" | "private";
    attributes?: Array<{ key: string; valueText?: string | null; valueNum?: number | null; valueBool?: boolean | null }>;
  }) =>
    apiPost<{ stockId: string; state: string }>("/admin/characters", { source: "generate", ...body }),
  activateCharacter: (id: string) => apiPost<{ stockId: string; isEnabled: boolean }>(`/admin/characters/${id}/activate`),
  deactivateCharacter: (id: string) => apiPost<{ stockId: string; isEnabled: boolean }>(`/admin/characters/${id}/deactivate`),
  delistCharacter: (id: string) => apiPost<{ stockId: string; isDelisted: boolean }>(`/admin/characters/${id}/delist`),
  relistCharacter: (id: string) => apiPost<{ stockId: string; isDelisted: boolean }>(`/admin/characters/${id}/relist`),
  setCharacterDefault: (id: string) => apiPost<{ stockId: string; isDefault: boolean }>(`/admin/characters/${id}/default`),
  regenerateCharacter: (id: string) => apiPost<{ stockId: string; state: string }>(`/admin/characters/${id}/regenerate`),
  // Embed a character into the casting index (vms_characters) so cast search can find it. Activating
  // a character auto-indexes it; use this to re-index after editing attributes. Best-effort backend:
  // ok:false means the embedder was off/hiccuped (the row is unaffected; retry later).
  reindexCharacter: (id: string) => apiPost<{ ok: boolean; castingDesc?: string; error?: string }>(`/admin/characters/${id}/reindex`),
  updateCharacterAttributes: (id: string, attributes: Array<{ key: string; valueText?: string | null; valueNum?: number | null; valueBool?: boolean | null }>) =>
    apiPut<{ stockId: string; attributes: number }>(`/admin/characters/${id}/attributes`, { attributes }),
  listVoices: () => apiGet<{ voices: VoiceOption[] }>("/admin/characters/voices"),
  /** (session 9) set a character's own voice (through the identity service) */
  setCharacterVoice: (id: string, voiceCatalogId: string) =>
    apiPut<{ stockId: string; voice: VoiceOption }>(`/admin/characters/${id}/voice`, { voiceCatalogId }),
  listNiches: () => apiGet<{ niches: Array<{ key: string; label: string }> }>("/admin/characters/niches"),
  listAttributesCatalog: () => apiGet<{ dimensions: AttributeCatalogDimension[] }>("/admin/characters/attributes-catalog"),
  listPersonas: () => apiGet<{ personas: Array<{ key: string; label: string }> }>("/admin/characters/personas"),
  listVerticals: () => apiGet<{ verticals: Array<{ key: string; label: string; personas: string[] }> }>("/admin/characters/verticals"),
  castRoster: (params: { vertical: string; gender?: string; limit?: number } & Record<string, string | number | undefined>) => {
    const q = new URLSearchParams();
    for (const [k, v] of Object.entries(params)) {
      if (v !== undefined && v !== null && v !== "") q.set(k, String(v));
    }
    return apiGet<{
      vertical: string;
      personas: string[];
      autoPick: string | null;
      roster: Array<{
        stockId: string; displayName: string | null; gender: string | null;
        previewUrl: string | null; capability: CharacterCapability;
        personaMatches: number; matchedPersonas: string[]; isDefault: boolean;
      }>;
    }>(`/admin/characters/roster?${q.toString()}`);
  },
};
